import type { ColumnConfig } from '../RingilDataTable';

export type SortDirection = 'ASC' | 'DESC';

export interface TableSort {
  sortField: string;
  sortDirection: SortDirection;
}

/**
 * A v2 column plus what only the v3 table understands. Kept assignable to {@link ColumnConfig}, so one
 * config array can feed either table while the beta switch decides which one renders.
 */
export interface ColumnConfigV3<TRow> extends ColumnConfig<TRow> {
  /**
   * Pins the column while the table scrolls sideways (a narrow split pane): `left` for the identity
   * column, `right` for the row actions. Only the first `left` column is pinned — a second one would
   * need the first one's width to know where to stick.
   */
  sticky?: 'left' | 'right';
  /**
   * Takes the width the other columns leave (the Figma's last column). Its `width` is then only its
   * minimum. Every other column keeps exactly its `width`, and text wraps or truncates inside it.
   */
  fill?: boolean;
  /** The user may drag the header's right edge to set this column's width (see `onResizeColumn`). */
  resizable?: boolean;
  /** The narrowest the user may drag it to (default DRAG_MIN_WIDTH) — what its content needs to stay whole. */
  minResizeWidth?: number;
  /**
   * Heading that is already translated — a configured column titled by its fields. Wins over
   * `header`/`headerDefault`, so a generated column id never reaches tr() as a translation key.
   */
  headerText?: string;
}

/** The column's heading as text (sort options, sidebars); `headerRender` may still draw it richer. */
export const headingText = (column: { headerText?: string; header?: string; headerDefault?: string }, tr: (key: string, fallback: string) => string): string =>
  column.headerText ?? (column.header || column.headerDefault ? tr(column.header, column.headerDefault) : '');

/** Columns the table draws, in display order: `hidden` ones only exist to feed the sort options. */
export const visibleColumns = <TRow>(columns: ColumnConfigV3<TRow>[]): ColumnConfigV3<TRow>[] =>
  columns.filter(column => !column.hidden).sort((a, b) => (a.order || 0) - (b.order || 0));

/**
 * The sort a header click asks for. Clicking the sorted column flips it; clicking another one starts
 * ascending — the same order the v2 (PrimeReact) table cycles through, and never back to "unsorted",
 * because the list API always sorts by something.
 */
export const nextSort = (current: TableSort, field: string): TableSort =>
  current.sortField === field ? { sortField: field, sortDirection: current.sortDirection === 'ASC' ? 'DESC' : 'ASC' } : { sortField: field, sortDirection: 'ASC' };

/** `aria-sort` for a header, so a screen reader announces which column orders the rows and how. */
export const ariaSortFor = (column: { sortable?: boolean; field?: string }, sort: TableSort): 'ascending' | 'descending' | 'none' | undefined => {
  if (!column.sortable || !column.field) return undefined;
  if (column.field !== sort.sortField) return 'none';
  return sort.sortDirection === 'ASC' ? 'ascending' : 'descending';
};

export interface StickyPlacement {
  side: 'left' | 'right';
  offset: number;
}

/**
 * Where each visible column is pinned, keyed by column id; unpinned columns are absent. The first
 * `left` column sticks to the left edge (the row checkbox lives inside it), later `left` ones scroll
 * normally. Every `right` column sticks to the right edge — in practice there is one, the actions.
 */
export const stickyPlacements = (columns: { id: string; sticky?: 'left' | 'right' }[]): Record<string, StickyPlacement> => {
  const placements: Record<string, StickyPlacement> = {};
  let leftTaken = false;
  for (const column of columns) {
    if (column.sticky === 'left' && !leftTaken) {
      placements[column.id] = { side: 'left', offset: 0 };
      leftTaken = true;
    } else if (column.sticky === 'right') {
      placements[column.id] = { side: 'right', offset: 0 };
    }
  }
  return placements;
};

const px = (width?: string): number => {
  const match = /^(\d+(?:\.\d+)?)px$/.exec((width ?? '').trim());
  return match ? Number(match[1]) : 0;
};

/**
 * The narrowest the table may get: every column at its width (a fill column at its minimum). Below
 * that the table scrolls sideways instead of squeezing columns — a column never grows to fit its
 * content either, which is what kept one long name from pushing the rest off screen.
 */
export const tableMinWidth = (columns: { width?: string }[]): number => columns.reduce((sum, column) => sum + px(column.width), 0);

/** State of the select-all checkbox for the rows on this page, counting only rows that can be selected. */
export const selectAllState = <TRow>(rows: TRow[], isSelected: (row: TRow) => boolean, isRowSelectable?: (row: TRow) => boolean): 'none' | 'some' | 'all' => {
  const selectable = isRowSelectable ? rows.filter(isRowSelectable) : rows;
  const selected = selectable.filter(isSelected).length;
  if (selected === 0) return 'none';
  return selected === selectable.length ? 'all' : 'some';
};

/**
 * Which pinned edges cast a shadow: only while content is actually scrolled under them, as the Figma
 * asks — a shadow over nothing reads as a stray line.
 */
export const pinnedShadows = (scroll: { scrollLeft: number; clientWidth: number; scrollWidth: number }): { left: boolean; right: boolean } => ({
  left: scroll.scrollLeft > 0,
  right: scroll.scrollLeft + scroll.clientWidth < scroll.scrollWidth - 1,
});

/** Room the row checkbox takes in the first cell: 16px box plus its 8px gap. */
export const CHECKBOX_SLOT = 24;

/** A pixel width grown by `extra`; non-pixel widths are left alone. */
export const addWidth = (width: string | undefined, extra: number): string | undefined => {
  const value = px(width);
  return value > 0 ? `${value + extra}px` : width;
};

export type RowTone = 'selected' | 'stripe' | 'plain';

/**
 * A row's background: selected wins; otherwise every second row is striped so a wide row is easy to
 * follow across the table. Counted per page from the first row, which is plain.
 */
export const rowTone = (index: number, selected: boolean, active = false): RowTone => (selected || active ? 'selected' : index % 2 === 1 ? 'stripe' : 'plain');

/**
 * Whether a click on a row should open it in the split view's detail pane. Controls inside the row
 * (buttons, inputs, the checkbox, links) keep doing their own thing — except the row's own detail
 * link (`data-row-open`), which opens the pane on a plain click and keeps the browser's new-tab
 * behaviour on Ctrl/Cmd/Shift or a middle click.
 */
export const rowClickIntent = (
  target: { closest: (selector: string) => unknown },
  event: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean; button: number },
): 'activate' | 'activate-instead-of-link' | 'ignore' => {
  const newTab = event.ctrlKey || event.metaKey || event.shiftKey || event.button === 1;
  if (target.closest('[data-row-open]')) return newTab ? 'ignore' : 'activate-instead-of-link';
  if (target.closest('a, button, input, select, textarea, label, [role="checkbox"], [role="button"], [role="switch"], [role="separator"]')) return 'ignore';
  return 'activate';
};

/** Range of a dragged column width — the same as the stored one (views/columnOps). */
export const DRAG_MIN_WIDTH = 60;
export const DRAG_MAX_WIDTH = 1200;

/** The width while dragging a header edge: where it started plus how far the pointer moved, kept in range. */
export const dragWidth = (startWidth: number, startX: number, x: number, min = DRAG_MIN_WIDTH): number =>
  Math.round(Math.min(DRAG_MAX_WIDTH, Math.max(min, startWidth + (x - startX))));

/** How far one ←/→ press moves the columns — about one narrow column. */
export const KEY_SCROLL_STEP = 120;

/**
 * How far a header arrow moves the columns: most of the visible scrolling part (the pinned columns
 * don't move), so a column cut at the edge is still in view after the jump and the eye can follow.
 */
export const pageScrollStep = (clientWidth: number, leftInset: number, rightInset: number): number =>
  Math.max(KEY_SCROLL_STEP, Math.round((clientWidth - leftInset - rightInset) * 0.7));

/**
 * The sideways scroll a key asks for: ←/→ alone. With a modifier the key belongs to someone else
 * (Alt+← is the browser's Back, Shift+→ extends a text selection), and in a field it moves the caret.
 */
export const keyScrollDelta = (event: { key: string; altKey: boolean; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }, typing: boolean): number => {
  if (typing || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return 0;
  if (event.key === 'ArrowLeft') return -KEY_SCROLL_STEP;
  if (event.key === 'ArrowRight') return KEY_SCROLL_STEP;
  return 0;
};

/** Focus is in a field, where arrow keys move the caret rather than the table. */
export const isTypingTarget = (target: { closest: (selector: string) => unknown } | null): boolean =>
  !!target?.closest('input, textarea, select, [contenteditable="true"]');
